// Top level libraries and 3rd party dependencies
import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { array, bool, func, number, object } from 'prop-types';
import { union } from 'ramda';

// Redux
import { connect } from 'react-redux';
import { getMessages, replyToConversation } from '../../actions/messages/messagesActions';
import { getSessionTime } from '../../actions/session/sessionActions';

import { withChatContext } from '../../context';
import Chat from './Chat';
import { MESSAGE_POLL_INTERVAL } from '../../constants/AppConstants';


class ChatContainer extends Component {
    static propTypes = {
        canReply: bool,
        error: object,
        replyToConversation: func.isRequired,
        getMessages: func,
        loadingState: object,
        location: object,
        logoutRequest: func,
        history: object,
        match: object,
        latestMessages: array,
        latestMessageId: number,
        sessionTime: number,
        timerStart: func,
    };

    constructor(props) {
        super(props);

        this.state = {
            messagesList: [],
            messageId: 0,
            time: {},
            seconds: 0,
        };

        this.polling = undefined;
        this.timer = 0;
    }

    // Component loads, starts browser-level session timer, starts message polling.
    componentDidMount() {
        this.props.getSessionTime();
        this.props.getMessages(0);
        this.polling = setInterval(this.pollMessages, MESSAGE_POLL_INTERVAL);
    }

    componentDidUpdate(prevProps) {
        const { error, sessionTime } = this.props;
        this.setState(this.updateMessagesIfNecessary);

        if (prevProps.sessionTime !== sessionTime && !error) {
            this.setState(this.mapSessionTimeRemaining, () => this.initBrowserSessionTimer());
        }
    }

    componentWillUnmount() {
        clearInterval(this.timer);
        clearInterval(this.polling);
    }

    countDown = () => {
        // Remove one second, set state so a re-render happens.
        let seconds = this.state.seconds - 1;
        this.setState({
            time: this.secondsToTime(seconds),
            seconds,
        }, () => {
            // Check if we're at zero.
            if (seconds === 0) {
                clearInterval(this.timer);
            }
        });
    };

    // Begin timer with sessionTimeRemaining from API
    initBrowserSessionTimer = () => {
        let timeLeftVar = this.secondsToTime(this.state.seconds);
        this.setState({ time: timeLeftVar }, () => this.startTimer());
    };

    pollMessages = () => {
        if (!this.props.error) {
            this.props.getMessages(this.props.latestMessageId);
        } else {
            clearInterval(this.polling);
        }
    };

    replyToConversationHandler = (message, attachment) => {
        let formData = new FormData();

        // Check for files
        if (attachment.length >= 1) {
            for (let i = 0; i < attachment.length; i++) {
                const file = attachment[i];
                formData.append('attachments', file);
            }
        }

        formData.append('message', message);

        this.props.replyToConversation(formData);
    };

    resetTimer = () =>
        this.setState({
            time: {},
            seconds: this.props.sessionTime,
        });

    startTimer = () => {
        if (this.timer === 0) {
            this.timer = setInterval(this.countDown, 1000);
        }
    };

    secondsToTime = secs => {
        let hours = Math.floor(secs / (60 * 60));
        let divisor_for_minutes = secs % (60 * 60);
        let minutes = Math.floor(divisor_for_minutes / 60);
        let divisor_for_seconds = divisor_for_minutes % 60;
        let seconds = ("0" + divisor_for_seconds).slice(-2); // to display as 00 instead of 0
        return {
            "h": hours,
            "m": minutes,
            "s": seconds
        };
    };

    updateMessagesIfNecessary = (state, props) => {
        const { latestMessages, latestMessageId } = props;
        let updatedMessageId = undefined, updatedMessagesList = undefined;

        // If the latest message id changed, update the state variable to reflect it
        if (this.state.messageId !== latestMessageId && latestMessageId) {
            updatedMessageId = latestMessageId;
        }

        // If the list of latest messages changed, append them to the existing messages list.
        if (this.state.messagesList !== latestMessages && latestMessages.length >= 1){
            updatedMessagesList = union(this.state.messagesList, latestMessages);
        }

        if (updatedMessageId && updatedMessagesList) {
            return {
                messageId: updatedMessageId,
                messagesList: updatedMessagesList,
            }
        } else {
            return null;
        }
    };

    mapSessionTimeRemaining = (state, props) => {
        if (props.sessionTime) {
            return {
                seconds: props.sessionTime
            }
        }
    };

    render() {
        const { conversationHash } = this.props.match.params;
        const { canReply, error, loadingState } = this.props;
        const sessionHasTimedOut = this.state.time && this.state.time.m === 0 && this.state.time.s === '00';
        return (
            sessionHasTimedOut ?
                <Redirect to={`/reverify/${conversationHash}`}/> :
                <Chat
                    canReply={canReply}
                    error={error}
                    loadingState={loadingState}
                    messagesList={this.state.messagesList}
                    sessionTime={this.state.time}
                    replyToConversationHandler={this.replyToConversationHandler}
                    resetSessionTime={this.resetTimer}
                    conversationHash={conversationHash}
                />
        );
    }
}

const mapStateToProps = state => ({
    canReply: state.messages.canReply,
    loadingState: state.loadingState,
    error: state.messages.error,
    sessionTime: state.session.time,
    latestMessages: state.messages.latestMessages,
    latestMessageId: state.messages.latestMessageId
});

const mapDispatchToProps = dispatch => ({
    replyToConversation: message => dispatch(replyToConversation(message)),
    getMessages: request => dispatch(getMessages(request)),
    getSessionTime: () => dispatch(getSessionTime()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withChatContext(ChatContainer));